import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mockVnGoldPrices } from '@/mocks/vnGoldPrices';

export interface VnGoldPrice {
  id: string;
  type_code: string;
  name: string;
  buy: number;
  sell: number;
  change_buy: number;
  change_sell: number;
  change_percent: number;
  updated_at: string;
}

export function useVnGoldPrices() {
  const [data, setData] = useState<VnGoldPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMockData, setIsMockData] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchPrices() {
      try {
        setLoading(true);
        setError(null);
        setIsMockData(false);

        const { data: rows, error: dbError } = await supabase
          .from('vn_gold_prices')
          .select('*')
          .order('type_code', { ascending: true });

        if (dbError) throw dbError;

        const prices = (rows || []) as VnGoldPrice[];

        if (!cancelled) {
          if (prices.length === 0) {
            setData(mockVnGoldPrices);
            setIsMockData(true);
          } else {
            setData(prices);
            setIsMockData(false);
          }
        }

        const now = Date.now();
        const hasStale = prices.some((p) => {
          const updated = new Date(p.updated_at).getTime();
          return now - updated > 10 * 60 * 1000;
        });

        if (prices.length > 0 && !hasStale) {
          // Trigger background update using Supabase client (includes auth headers)
          supabase.functions.invoke('update-gold-vn-prices', {
            method: 'POST',
          }).catch(() => {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải giá vàng');
          setData(mockVnGoldPrices);
          setIsMockData(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPrices();
    return () => { cancelled = true; };
  }, []);

  return { data, loading, error, isMockData };
}